import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { LetterPdfViewer } from "@/components/letters/letter-pdf-viewer";
import { NoticeEnclosuresList } from "@/components/notices/notice-enclosures-list";
import { buttonClasses, LoadError, PageTitle, Skeleton, StatusChip } from "@/components/ui";
import { useActiveMembership, useMyCustomerContext } from "@/hooks/use-customer-context";
import { useCustomerNotice } from "@/hooks/use-customer-notices";
import { formatLongInstantDay } from "@/lib/dates";
import { deviceLink, lettersLink } from "@/lib/destinations";
import { letterFileName } from "@/lib/letter-file-name";
import { LETTER_STATUS_TONE } from "@/lib/letter-status-tone";
import { present } from "@/lib/present";
import { uuidParam } from "@/lib/search-params";

export const Route = createFileRoute("/_customer/letters/$noticeId")({
  component: LetterPage,
});

const PAGE = "flex flex-col gap-5 lg:gap-6";

// The loaded page's shape in static blocks: the title and its line, then the PDF.
const LOADING = (
  <div className={PAGE}>
    <div>
      <Skeleton height={34} width="55%" />
      <Skeleton height={18} width="30%" className="mt-2" />
    </div>
    <Skeleton variant="card" height={320} />
  </div>
);

/**
 * One letter: its subject, how it was delivered and the day it was sent, who it
 * is addressed to and the device it is about, then the PDF (framed on desktop,
 * a download on phones) and the reports it encloses. The utility that sent it
 * dates and signs it: the membership naming the letter's org, the active one
 * only when none does. An id that cannot be a letter is not found without a
 * read.
 */
function LetterPage() {
  const { t, i18n } = useTranslation("customer");
  const { noticeId } = Route.useParams();
  const id = uuidParam(noticeId) ?? "";
  const read = useCustomerNotice(id);
  const { data: context } = useMyCustomerContext();
  const active = useActiveMembership();
  const notice = read.data;

  if (!id || read.error?.status === 404) {
    return (
      <div
        role="alert"
        className="rounded-card border border-line bg-surface p-4 shadow-card lg:p-5"
      >
        <h1 className="text-lg font-semibold tracking-[-0.01em] text-ink">
          {t("letterDetail.notFound")}
        </h1>
        <Link {...lettersLink()} className={`mt-3 ${buttonClasses("secondary")}`}>
          {t("nav.tabs.letters")}
        </Link>
      </div>
    );
  }

  if (read.isError) {
    return (
      <LoadError
        asPage
        title={t("letterDetail.error")}
        body={t("loadError.body")}
        onRetry={() => void read.refetch()}
      />
    );
  }

  if (!notice) return LOADING;

  const owning = context?.memberships.find((m) => m.org_id === notice.org_id) ?? active;
  const timezone = owning?.org_timezone;
  const title = present(notice.subject) ?? t("letterRow.untitled", { org: owning?.org_name ?? "" });
  const recipient = present(notice.recipient_name);

  return (
    <div className={PAGE}>
      <PageTitle
        title={title}
        sub={
          <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <StatusChip
              tone={LETTER_STATUS_TONE[notice.status]}
              label={t(`notices.status.${notice.status}`)}
            />
            <span>{formatLongInstantDay(notice.generated_at, timezone, i18n.language)}</span>
          </span>
        }
        back={{ label: t("nav.tabs.letters"), link: lettersLink() }}
        trail={[{ label: t("nav.tabs.letters"), link: lettersLink() }, { label: title }]}
      />
      {(recipient || notice.device_installation_id) && (
        <div className="flex flex-col items-start gap-1 text-sm text-body">
          {recipient && <p>{t("letterDetail.to", { name: recipient })}</p>}
          {notice.device_installation_id && (
            <Link
              {...deviceLink(notice.device_installation_id)}
              className="inline-flex min-h-11 items-center font-medium text-brand-fg underline-offset-2 hover:underline"
            >
              {t("letterDetail.device")}
            </Link>
          )}
        </div>
      )}
      <LetterPdfViewer
        noticeId={notice.id}
        hasPdf={notice.has_pdf ?? false}
        title={title}
        fileName={letterFileName(title, notice.generated_at, timezone)}
      />
      <NoticeEnclosuresList enclosures={notice.enclosures} />
    </div>
  );
}
